import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { withAuthRetry } from "src/pages/helpers";
import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { toast } from "react-toastify";
import {
  deleteThreadById,
  fetchAllThreads,
  fetchDocumentLibrary,
  fetchDraftDownloadUrl,
  fetchReferenceDocDownloadUrl,
  fetchThreadTitle,
  fetchUnreadThreads,
  markThreadReadById,
  renameDraftDocument,

  updateThreadTitle as updateThreadTitleApi,
} from "src/services/threads";
import { REQUEST_STATUS } from "src/domain/messages/requestStatus";
import { trackDocumentDownloaded } from "src/utils/documentAnalytics";

export const getAllThreads = createAsyncThunk("threads/getAllThreads", async (data, thunkAPI) => {
  try {
    return await withAuthRetry(async (accessToken) => {
      return {
        data: data,
        response: await fetchAllThreads(accessToken),
      };
    });
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, data: data });
  }
});

export const getAllGeneratedDocs = createAsyncThunk("threads/getAllGeneratedDocs", async (data, thunkAPI) => {
  try {
    return await withAuthRetry(async (accessToken) => {
      if (accessToken) {
        return {
          data,
          response: await fetchDocumentLibrary(accessToken),
        };
      }
      return null;
    });
  } catch (error) {
    return thunkAPI.rejectWithValue({ error, data });
  }
});

export const deleteThread = createAsyncThunk("chat/deleteThread", async (data, thunkAPI) => {
  try {
    return { response: await deleteThreadById(data.id), request: data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, request: data });
  }
});

export const updateTitle = createAsyncThunk("threads/updateTitle", async (data, thunkAPI) => {
  try {
    return {
      response: await updateThreadTitleApi(data.id, data),
      thread_id: data.id,
      request: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, request: data });
  }
});

export const markThreadRead = createAsyncThunk("threads/markThreadRead", async (data, thunkAPI) => {
  try {
    return {
      response: await markThreadReadById(data.threadId),
      request: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const getTitle = createAsyncThunk("threads/getTitle", async (data, thunkAPI) => {
  try {
    return {
      response: await fetchThreadTitle(data.thread[0]),
      index: data.thread[1],
      mode: data.mode,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const generateDownloadDraftUrl = createAsyncThunk("threads/generateDownloadDraftUrl", async (data, thunkAPI) => {
  try {
    const response = await fetchDraftDownloadUrl(data.threadId, data.type);
    // Legacy draft download (no versioned document): only format is known.
    trackDocumentDownloaded({
      format: data.type,
      threadId: data.threadId,
      metadata: thunkAPI.getState().onboarding?.userMetadata,
    });
    return { response, request: data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const generateReferenceDocDownloadUrl = createAsyncThunk(
  "threads/generateReferenceDocDownloadUrl",
  async ({ threadId, storageId }, thunkAPI) => {
    try {
      return { response: await fetchReferenceDocDownloadUrl(threadId, storageId), request: storageId };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error });
    }
  },
);

export const renameDocument = createAsyncThunk(
  "messageSlice/renameDocument",
  async ({ threadId, newName }, thunkAPI) => {
    try {
      return { data: await renameDraftDocument(threadId, newName), threadId, newName };
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.response?.data || error.message });
    }
  },
);

export const getUnreadThreads = createAsyncThunk("threads/getUnreadThreads", async (data, thunkAPI) => {
  try {
    return {
      data: data,
      response: await fetchUnreadThreads(),
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, data: data });
  }
});

const ThreadsSlice = createSlice({
  name: "threads",
  initialState: {
    getTitleStatus: "idle",
    getTitleError: null,

    acceptLegalReviewStatus: "idle",
    acceptLegalReviewError: null,

    rejectLegalReviewStatus: "idle",
    rejectLegalReviewError: null,
    downloadingDocumentThreadId: null,
    threads: [],
    deletedThread: {},
    titleUpdated: {},
    threads_status: "idle",
    currentThread: null,
    unreadThreads: [],
    unreadThreadsFetchStatus: "idle",
    markThreadReadStatus: "idle",
    showChatActionModal: false,
    downloadDraftUrl: "",
    downloadDraftUrlStatus: "idle",
    downloadDraftUrlError: "",
    referenceDocDownloadUrl: {},
    openSignupPopup: false,
    storeConversationPopup: false,
    generatedDocs: [],
    getAllGeneratedDocsStatus: "idle",
    getAllGeneratedDocsError: null,
    documentRenameStatus: "idle",
    documentRenameError: null,
  },
  reducers: {
    resetData: (state) => {
      state.deletedThread = {};
      state.titleUpdated = {};
    },
    setShowChatActionModal: (state, action) => {
      state.showChatActionModal = action.payload;
    },
    resetReferenceDocDownloadUrl: (state) => {
      state.referenceDocDownloadUrl = {};
    },
    resetDownloadUrl: (state) => {
      state.downloadDraftUrl = "";
      state.downloadDraftUrlStatus = "idle";
      state.downloadDraftUrlError = "";
    },

    insertNewThread: (state, action) => {
      var data = {
        id: action.payload.id,
        type: action.payload.thread_type,
        title: "New thread",
        document_storage_id: null,
        is_shared: false,
        messaging_disabled: action.payload.messaging_disabled,
        user: "user",
        is_user: true,
        created_at: new Date(),
      };
      try {
        state.threads["today"] = [data, ...state.threads["today"]];
      } catch (e) {
        // // console.log("Error in inserting new thread", e);
      }
    },

    updateThreadTitleLocal: (state, action) => {
      var threadId = action.payload.thread_id;
      var title = action.payload.title;
      var duration = null;
      var index = null;
      for (var key in state.threads) {
        index = state.threads[key].findIndex((thread) => thread.id === threadId);
        if (index !== -1) {
          duration = key;
          break;
        }
      }
      if (index > -1 && duration) {
        // state.threads[duration][index].title = title;
        var durations = state.threads[duration];
        var thread = durations[index];
        thread["title"] = title;
        durations[index] = thread;
        state.threads = { ...state.threads, [duration]: durations };
      }
    },
    setDownloadingDocumentThreadId: (state, action) => {
      state.downloadingDocumentThreadId = action.payload;
    },
    updateThreadFeedBackLocal: (state, action) => {
      if (action.payload.mode === "ask") {
        let tempArray = [...state.askThreadsList];
        let index = tempArray.findIndex((thread) => thread.id === action.payload.threadId);

        // Check if a thread was found
        if (index !== -1) {
          tempArray[index].legal_review_feedback = action.payload.legal_review_feedback;

          state.askThreadsList = tempArray;
        } else {
          console.warn("Thread not found in askThreadsList with ID:", action.payload.threadId);
        }
      } else {
        let tempArray = [...state.draftThreadsList];
        let index = tempArray.findIndex((thread) => thread.id === action.payload.threadId);

        // Check if a thread was found
        if (index !== -1) {
          tempArray[index].legal_review_feedback = action.payload.legal_review_feedback;

          state.draftThreadsList = tempArray;
        } else {
          console.warn("Thread not found in draftThreadsList with ID:", action.payload.threadId);
        }
      }
    },
    addNewThreadLocal: (state, action) => {
      if (action.payload.mode === "ask") {
        state.askThreadsList = [action.payload.data, ...state.askThreadsList];
      } else {
        state.draftThreadsList = [action.payload.data, ...state.draftThreadsList];
      }
    },
    removeDeletedThreadFromList: (state, action) => {
      if (action.payload.mode === "ask") {
        state.askThreadsList = state.askThreadsList.filter((thread) => thread.id !== action.payload.threadId);
      } else {
        state.draftThreadsList = state.draftThreadsList.filter((thread) => thread.id !== action.payload.threadId);
      }
    },
    resetLegalReviewStatus: (state) => {
      state.acceptLegalReviewStatus = "idle";
      state.acceptLegalReviewError = null;
      state.rejectLegalReviewStatus = "idle";
      state.rejectLegalReviewError = null;
    },
    addUnreadThread: (state, action) => {
      if (state.unreadThreads.indexOf(action.payload) === -1) {
        state.unreadThreads.push(action.payload);
      }
    },

    deleteUnreadThread: (state, action) => {
      state.unreadThreads = state.unreadThreads.filter((thread) => thread !== action.payload);
    },
    resetAnimation: (state, action) => {
      if (action.payload.mode === "ask") {
        let tempArray = [...state.askThreadsList];
        let index = tempArray.findIndex((thread) => thread.id === action.payload.threadId);

        // Check if a thread was found
        if (index !== -1) {
          // tempArray[index].title = action.payload.title;
          // if (action.payload?.animate) {
          tempArray[index].animate = action.payload.animate;
          // }
          state.askThreadsList = tempArray;
        } else {
          console.warn("Thread not found in askThreadsList with ID:", action.payload.threadId);
        }
      } else {
        let tempArray = [...state.draftThreadsList];
        let index = tempArray.findIndex((thread) => thread.id === action.payload.threadId);

        // Check if a thread was found
        if (index !== -1) {
          // tempArray[index].title = action.payload.title;
          // if (action.payload?.animate) {
          tempArray[index].animate = action.payload.animate;
          // }
          state.draftThreadsList = tempArray;
        } else {
          console.warn("Thread not found in draftThreadsList with ID:", action.payload.threadId);
        }
      }
    },
    resetUpdateActions: (state) => {
      state.deletedThread = {};
      state.updatedTitle = {};
    },
    resetDeletedThread: (state) => {
      state.deletedThread = {};
    },
    resetUpdatedTitle: (state) => {
      state.updatedTitle = {};
    },
    setOpenSignupPopup: (state, action) => {
      state.openSignupPopup = action.payload;
    },
    setStoreConversationPopup: (state, action) => {
      state.storeConversationPopup = action.payload;
    },
    setThreadPrivileged: (state, action) => {
      const threadId = action.payload;
      for (const key in state.threads) {
        const index = state.threads[key].findIndex((thread) => thread.id === threadId);
        if (index !== -1) {
          state.threads[key][index].is_privileged = true;
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllThreads.pending, (state, action) => {
        state.threads_status = REQUEST_STATUS.LOADING;
        // state.threads = [];
      })
      .addCase(getAllThreads.rejected, (state, action) => {
        // state.threads = [];
        state.threads_status = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action.payload.error, "get_all_threads_error");
        } else {
          // Log authentication timing issues separately
          const authError = {
            error: "Authentication failed for getAllThreads",
            status: action.payload?.error?.response?.status,
            hasToken: !!localStorage.getItem("accessToken2"),
            tokenLength: localStorage.getItem("accessToken2")?.length || 0,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          };
          ErrorLoggingV2(authError, "get_all_threads_auth_error");
        }
      })
      .addCase(getAllThreads.fulfilled, (state, action) => {
        var results = action.payload.response;
        state.threads = results;
        state.threads_status = "success";
      })

      // getTitle handlers
      .addCase(getTitle.pending, (state) => {
        state.getTitleStatus = "loading";
        state.getTitleError = "";
      })
      .addCase(getTitle.fulfilled, (state, action) => {
        state.getTitleStatus = "idle";
        state.getTitleError = "";
        if (action.payload.mode === "ask") {
          state.askThreadsList[action.payload.index].title = action.payload.response.title;
        } else {
          state.draftThreadsList[action.payload.index].title = action.payload.response.title;
        }
      })
      .addCase(getTitle.rejected, (state, action) => {
        state.getTitleStatus = "failed";
        state.getTitleError = action.payload.error;
        ErrorLoggingV2(action?.payload?.error, "get_title_error");
      })
      .addCase(generateDownloadDraftUrl.pending, (state) => {
        state.downloadDraftUrlStatus = "loading";
        state.downloadDraftUrlError = "";
      })
      .addCase(generateDownloadDraftUrl.fulfilled, (state, action) => {
        state.downloadDraftUrl = action.payload.response.url;
        const downloadFile = async (url) => {
          try {
            const downloadViaIframe = (url) => {
              const iframe = document.createElement("iframe");
              iframe.style.display = "none";
              iframe.src = url;
              document.body.appendChild(iframe);
            };
            downloadViaIframe(action.payload.response.url);

            state.downloadDraftUrlStatus = "success";
            state.downloadingDocumentThreadId = null;
          } catch (error) {
            window.open(action.payload.response.url, "_blank");
            state.downloadDraftUrlStatus = "success";

            state.downloadingDocumentThreadId = null;
          }
        };
        downloadFile(action.payload.response.url);
      })
      .addCase(generateDownloadDraftUrl.rejected, (state, action) => {
        state.downloadDraftUrlStatus = "failed";
        state.downloadDraftUrl = "";
        state.downloadDraftUrlError = "Failed to generate download link";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "generate_download_draft_url_error");
        }

        state.downloadingDocumentThreadId = null;
      })

      // deleteThread handlers
      .addCase(deleteThread.pending, (state) => {
        state.deletedThread = { status: "loading" };
      })
      .addCase(deleteThread.fulfilled, (state, action) => {
        var threadId = action.payload.request.id;
        var duration = action.payload.request.duration;

        var durationThreads = state.threads[duration].filter((thread) => thread.id !== threadId);
        state.threads = { ...state.threads, [duration]: durationThreads };

        // state.threads = state.threads[duration].filter((thread) => thread.id !== threadId);
        state.deletedThread = {
          id: threadId,
          duration: duration,
          status: "success",
        };
      })
      .addCase(deleteThread.rejected, (state, action) => {
        state.deletedThread = { error: "Failed to delete thread", status: "rejected" };
        ErrorLoggingV2(action?.payload?.error, "delete_thread_error");
      })

      // updateTitle handlers
      .addCase(updateTitle.pending, (state) => {
        state.updatedTitle = { status: "loading" };
      })
      .addCase(updateTitle.fulfilled, (state, action) => {
        var threadId = action.payload.request.id;
        var duration = action.payload.request.duration;
        var title = action.payload.request.title;
        var index = state.threads[duration].findIndex((thread) => thread.id === threadId);
        var durationThreads = state.threads[duration];
        durationThreads[index].title = title;
        state.threads = { ...state.threads, [duration]: durationThreads };
        state.updatedTitle = { status: "success", id: threadId, duration: duration, title: title };
        // state.threads[duration]
        // state.threads = state.threads[duration].map((thread) => thread.id === threadId ? { ...thread, title: title } : thread);
        // var durationThreads = state.threads[duration];
        // var index = durationThreads.findIndex((thread) => thread.id === threadId);
        // durationThreads[index].title = title;
        // state.threads = { ...state.threads, [duration]: durationThreads };
      })
      .addCase(updateTitle.rejected, (state, action) => {
        state.updatedTitle = { error: "Failed to update title", status: "rejected" };
        ErrorLoggingV2(action?.payload?.error, "update_title_error");
      })

      // get all unread thrads
      .addCase(getUnreadThreads.pending, (state) => {
        state.unreadThreadsFetchStatus = "loading";
        // state.unreadThreads = [];
      })
      .addCase(getUnreadThreads.fulfilled, (state, action) => {
        state.unreadThreadsFetchStatus = "success";
        state.unreadThreads = action.payload.response;
      })
      .addCase(getUnreadThreads.rejected, (state, action) => {
        state.unreadThreadsFetchStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "get_unread_threads_error");
        }
      })

      // markthread read
      .addCase(markThreadRead.pending, (state) => {
        state.markThreadReadStatus = "loading";
        // state.unreadThreads = [];
      })
      .addCase(markThreadRead.fulfilled, (state, action) => {
        state.markThreadReadStatus = "success";
      })
      .addCase(markThreadRead.rejected, (state, action) => {
        state.markThreadReadStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "mark_thread_read_error");
        }
      })

      .addCase(generateReferenceDocDownloadUrl.pending, (state) => {
        state.referenceDocDownloadUrl = {};
      })
      .addCase(generateReferenceDocDownloadUrl.fulfilled, (state, action) => {
        state.referenceDocDownloadUrl = {
          url: action.payload.response.url,
          reference_doc_storage_id: action.payload.request,
        };
      })
      .addCase(generateReferenceDocDownloadUrl.rejected, (state, action) => {
        state.referenceDocDownloadUrl = {};
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "generate_reference_doc_download_url_error");
        }
      })
      .addCase(getAllGeneratedDocs.pending, (state) => {
        state.getAllGeneratedDocsStatus = "loading";
      })
      .addCase(getAllGeneratedDocs.fulfilled, (state, action) => {
        state.getAllGeneratedDocsStatus = "success";
        state.generatedDocs = action.payload.response;
      })
      .addCase(getAllGeneratedDocs.rejected, (state, action) => {
        state.getAllGeneratedDocsStatus = "failed";
        state.getAllGeneratedDocsError = action.payload.error;

        if (
          action.payload?.error?.response?.status !== 401 &&
          JSON.stringify(action.payload.error) !== JSON.stringify({})
        ) {
          ErrorLoggingV2(action.payload.error, "get_all_generated_docs_error");
        } else {
          // Log authentication timing issues separately
          const authError = {
            error: "Authentication failed for getAllGeneratedDocs",
            status: action.payload?.error?.response?.status,
            hasToken: !!localStorage.getItem("accessToken2"),
            tokenLength: localStorage.getItem("accessToken2")?.length || 0,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          };
          ErrorLoggingV2(authError, "get_all_generated_docs_auth_error");
        }
      })
      .addCase(renameDocument.pending, (state) => {
        state.documentRenameStatus = "loading";
        state.documentRenameError = null;
      })
      .addCase(renameDocument.fulfilled, (state, action) => {
        const { threadId, newName } = action.payload;
        // Support both flat (legacy) and nested (grouped) document structures
        for (const group of state.generatedDocs) {
          if (group.documents) {
            const doc = group.documents.find((d) => d.document_id === threadId);
            if (doc) {
              doc.document_name = newName;
              break;
            }
          }
          // Legacy flat structure fallback
          if (group.thread_id === threadId) {
            group.document_name = newName;
            break;
          }
        }
        state.documentRenameStatus = "success";
        toast.success("Document name updated successfully!");
      })
      .addCase(renameDocument.rejected, (state, action) => {
        state.documentRenameStatus = "failed";
        state.documentRenameError = action.payload || "Rename failed";
        toast.error("Failed to rename document. Please try again.");
      });
  },
});

export default ThreadsSlice.reducer;
export const {
  resetLegalReviewStatus,
  addUnreadThread,
  deleteUnreadThread,
  removeDeletedThreadFromList,
  updateThreadTitleLocal,
  addNewThreadLocal,
  updateThreadFeedBackLocal,
  setShowChatActionModal,
  resetAnimation,
  resetDeletedThread,
  resetData,
  resetUpdatedTitle,
  insertNewThread,
  resetUpdateActions,
  resetDownloadUrl,
  resetReferenceDocDownloadUrl,
  setOpenSignupPopup,
  setStoreConversationPopup,
  setDownloadingDocumentThreadId,
  setThreadPrivileged,
} = ThreadsSlice.actions;
